import {
  HOW_DID_YOU_FIND_OUT_ABOUT_BUXUS_OPTIONS,
  type RegistrationExtendedFormValues,
} from "@buxus/shared";
import { RegReqLabel } from "../../ui/RegReqLabel";

type AdditionalQuestionsFieldKey =
  | "howDidYouFindOutAboutBuxus"
  | "howDidYouFindOutAboutBuxusAgency"
  | "howDidYouFindOutAboutBuxusAssociation"
  | "howDidYouFindOutAboutBuxusConference"
  | "howDidYouFindOutAboutBuxusOperator"
  | "howDidYouFindOutAboutBuxusTrainer"
  | "howDidYouFindOutAboutBuxusWebinar";

type Props = {
  extendedForm: RegistrationExtendedFormValues;
  showFieldError: (field: AdditionalQuestionsFieldKey) => string | undefined;
  onPatch: (partial: Partial<RegistrationExtendedFormValues>) => void;
  onBlur: (field: AdditionalQuestionsFieldKey) => void;
};

const CLEARED_CONDITIONALS: Partial<RegistrationExtendedFormValues> = {
  howDidYouFindOutAboutBuxusAgency: "",
  howDidYouFindOutAboutBuxusAssociation: "",
  howDidYouFindOutAboutBuxusConference: "",
  howDidYouFindOutAboutBuxusOperator: "",
  howDidYouFindOutAboutBuxusTrainer: "",
  howDidYouFindOutAboutBuxusWebinar: "",
};

export function AdditionalQuestionsStep({
  extendedForm,
  showFieldError,
  onPatch,
  onBlur,
}: Props) {
  const source = extendedForm.howDidYouFindOutAboutBuxus;

  return (
    <>
      <h2 className="reg-step-title">Additional Questions</h2>
      <div className="reg-grid">
        <label className="reg-field reg-field--full">
          <RegReqLabel required>How did you find out about Buxus®?</RegReqLabel>
          <select
            value={extendedForm.howDidYouFindOutAboutBuxus}
            onChange={(e) => {
              const next = e.target.value;
              onPatch({
                ...CLEARED_CONDITIONALS,
                howDidYouFindOutAboutBuxus: next,
              });
            }}
            onBlur={() => onBlur("howDidYouFindOutAboutBuxus")}
          >
            <option value="">Select…</option>
            {HOW_DID_YOU_FIND_OUT_ABOUT_BUXUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {showFieldError("howDidYouFindOutAboutBuxus") ? (
            <small className="reg-field-error">{showFieldError("howDidYouFindOutAboutBuxus")}</small>
          ) : null}
        </label>

        {source === "Agency" ? (
          <label className="reg-field reg-field--full">
            <RegReqLabel required>What agency?</RegReqLabel>
            <input
              value={extendedForm.howDidYouFindOutAboutBuxusAgency}
              onChange={(e) => onPatch({ howDidYouFindOutAboutBuxusAgency: e.target.value })}
              onBlur={() => onBlur("howDidYouFindOutAboutBuxusAgency")}
            />
            {showFieldError("howDidYouFindOutAboutBuxusAgency") ? (
              <small className="reg-field-error">
                {showFieldError("howDidYouFindOutAboutBuxusAgency")}
              </small>
            ) : null}
          </label>
        ) : null}

        {source === "Association" ? (
          <label className="reg-field reg-field--full">
            <RegReqLabel required>What association?</RegReqLabel>
            <input
              value={extendedForm.howDidYouFindOutAboutBuxusAssociation}
              onChange={(e) =>
                onPatch({ howDidYouFindOutAboutBuxusAssociation: e.target.value })
              }
              onBlur={() => onBlur("howDidYouFindOutAboutBuxusAssociation")}
            />
            {showFieldError("howDidYouFindOutAboutBuxusAssociation") ? (
              <small className="reg-field-error">
                {showFieldError("howDidYouFindOutAboutBuxusAssociation")}
              </small>
            ) : null}
          </label>
        ) : null}

        {source === "Conference" ? (
          <label className="reg-field reg-field--full">
            <RegReqLabel required>What conference?</RegReqLabel>
            <input
              value={extendedForm.howDidYouFindOutAboutBuxusConference}
              onChange={(e) =>
                onPatch({ howDidYouFindOutAboutBuxusConference: e.target.value })
              }
              onBlur={() => onBlur("howDidYouFindOutAboutBuxusConference")}
            />
            {showFieldError("howDidYouFindOutAboutBuxusConference") ? (
              <small className="reg-field-error">
                {showFieldError("howDidYouFindOutAboutBuxusConference")}
              </small>
            ) : null}
          </label>
        ) : null}

        {source === "Operator" ? (
          <label className="reg-field reg-field--full">
            <RegReqLabel required>What&apos;s the name of the operator?</RegReqLabel>
            <input
              value={extendedForm.howDidYouFindOutAboutBuxusOperator}
              onChange={(e) => onPatch({ howDidYouFindOutAboutBuxusOperator: e.target.value })}
              onBlur={() => onBlur("howDidYouFindOutAboutBuxusOperator")}
            />
            {showFieldError("howDidYouFindOutAboutBuxusOperator") ? (
              <small className="reg-field-error">
                {showFieldError("howDidYouFindOutAboutBuxusOperator")}
              </small>
            ) : null}
          </label>
        ) : null}

        {source === "Training" ? (
          <label className="reg-field reg-field--full">
            <RegReqLabel required>What is or was the name of the trainer?</RegReqLabel>
            <input
              value={extendedForm.howDidYouFindOutAboutBuxusTrainer}
              onChange={(e) => onPatch({ howDidYouFindOutAboutBuxusTrainer: e.target.value })}
              onBlur={() => onBlur("howDidYouFindOutAboutBuxusTrainer")}
            />
            {showFieldError("howDidYouFindOutAboutBuxusTrainer") ? (
              <small className="reg-field-error">
                {showFieldError("howDidYouFindOutAboutBuxusTrainer")}
              </small>
            ) : null}
          </label>
        ) : null}

        {source === "Webinar" ? (
          <label className="reg-field reg-field--full">
            <RegReqLabel required>Who hosted the webinar?</RegReqLabel>
            <input
              value={extendedForm.howDidYouFindOutAboutBuxusWebinar}
              onChange={(e) => onPatch({ howDidYouFindOutAboutBuxusWebinar: e.target.value })}
              onBlur={() => onBlur("howDidYouFindOutAboutBuxusWebinar")}
            />
            {showFieldError("howDidYouFindOutAboutBuxusWebinar") ? (
              <small className="reg-field-error">
                {showFieldError("howDidYouFindOutAboutBuxusWebinar")}
              </small>
            ) : null}
          </label>
        ) : null}
      </div>
    </>
  );
}
